import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";

const LINKS = [
    { to: "/", label: "Home" },
    { to: "/about", label: "About" },
    { to: "/products", label: "Products" },
    { to: "/lyanka-ai", label: "Lyanka AI" },
    { to: "/vision", label: "Vision" },
    { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    return (
        <header
            data-testid="site-navbar"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
                scrolled || open ? "bg-[#0a0a0b]/90 backdrop-blur-xl border-b border-white/[0.07]" : "bg-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between h-[72px]">
                <Link to="/" className="flex items-center gap-3" data-testid="navbar-logo-link">
                    <img src="/assets/ana-emblem.png" alt="ANA International logo" className="h-10 w-10 object-contain" />
                    <div className="leading-tight">
                        <span className="font-display font-bold tracking-[0.18em] text-sm block">ANA</span>
                        <span className="text-[10px] tracking-[0.32em] text-[#a3a29b] uppercase">International</span>
                    </div>
                </Link>

                <nav className="hidden lg:flex items-center gap-8" data-testid="navbar-desktop-nav">
                    {LINKS.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className={({ isActive }) =>
                                `text-[13px] tracking-wide transition-colors duration-300 ${
                                    isActive ? "text-[#e8c766]" : "text-[#c9c8c2] hover:text-white"
                                }`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                    <Link to="/lyanka-ai" className="btn-gold !py-2.5 !px-5 !text-[13px]" data-testid="nav-cta-lyanka">
                        Explore Lyanka AI <ArrowUpRight size={15} />
                    </Link>
                </nav>

                <button
                    className="lg:hidden text-white p-2"
                    onClick={() => setOpen(!open)}
                    aria-label="Toggle menu"
                    data-testid="navbar-mobile-toggle"
                >
                    {open ? <X size={22} /> : <Menu size={22} />}
                </button>
            </div>

            {open && (
                <div className="lg:hidden border-t border-white/[0.07] bg-[#0a0a0b]/95 backdrop-blur-xl" data-testid="navbar-mobile-menu">
                    <div className="px-6 py-6 flex flex-col gap-5">
                        {LINKS.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                                className={({ isActive }) =>
                                    `text-base ${isActive ? "text-[#e8c766]" : "text-[#c9c8c2]"}`
                                }
                            >
                                {l.label}
                            </NavLink>
                        ))}
                        <Link to="/lyanka-ai" className="btn-gold w-fit mt-2" data-testid="mobile-nav-cta-lyanka">
                            Explore Lyanka AI <ArrowUpRight size={15} />
                        </Link>
                    </div>
                </div>
            )}
        </header>
    );
};
